import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface AdminUser {
  id: string
  name: string
  email: string
  role: string
  status: string
  permissions: {
    can_manage_agencies: boolean
    can_manage_smbs: boolean
    can_view_analytics: boolean
    can_manage_system: boolean
  }
  created_at: string
}

interface AdminContextType {
  admin: AdminUser | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  isAuthenticated: boolean
}

const AdminContext = createContext<AdminContextType | undefined>(undefined)

interface AdminProviderProps {
  children: ReactNode
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<AdminUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check if admin is already authenticated
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('admin_access_token')
      if (token) {
        // Get current admin from API
        const response = await fetch('/api/v1/admin/auth/me', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (response.ok) {
          const adminData = await response.json()
          setAdmin(adminData)
        } else {
          // Clear invalid token
          localStorage.removeItem('admin_access_token')
          localStorage.removeItem('admin_user')
        }
      }
    } catch (error) {
      console.error('Admin auth check failed:', error)
      // Clear invalid token
      localStorage.removeItem('admin_access_token')
      localStorage.removeItem('admin_user')
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch('/api/v1/admin/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })
      
      if (!response.ok) {
        // Try to parse structured validation errors (e.g., 422 from FastAPI)
        let errorText = 'Login failed'
        try {
          const errorData = await response.json()
          if (errorData?.detail) {
            // FastAPI returns an array of validation errors for 422
            if (Array.isArray(errorData.detail)) {
              errorText = errorData.detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ')
            } else if (typeof errorData.detail === 'string') {
              errorText = errorData.detail
            }
          } else if (errorData?.message) {
            errorText = errorData.message
          }
        } catch (e) {
          // fallback
        }
        throw new Error(errorText)
      }

      const data = await response.json()
      
      // Store token and admin data
      localStorage.setItem('admin_access_token', data.access_token)
      localStorage.setItem('admin_user', JSON.stringify(data.admin))
      
      setAdmin(data.admin)
    } catch (error: any) {
      console.error('Admin login failed:', error)
      throw new Error(error.message || 'Login failed')
    }
  }

  const logout = () => {
    localStorage.removeItem('admin_access_token')
    localStorage.removeItem('access_token')
    localStorage.removeItem('admin_user')
    setAdmin(null)
  }

  const isAuthenticated = !!admin

  return (
    <AdminContext.Provider value={{
      admin,
      isLoading,
      login,
      logout,
      isAuthenticated
    }}>
      {children}
    </AdminContext.Provider>
  )
}

export const useAdmin = () => {
  const context = useContext(AdminContext)
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider')
  }
  return context
}
